import { useMutation } from '@apollo/client';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { InsertObligationChangeAttestationOneDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import {
  type InsertObligationChangeAttestationCompatibilityResult,
  type InsertObligationChangeAttestationTRPCInput,
  useInsertObligationChangeAttestationTRPC,
} from './useInsertObligationChangeAttestationTRPC';

export const useInsertObligationChangeAttestation = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const { user } = useRisksmartUser();

  // GraphQL mutation
  // COI-577: moved off the detail page so every consumer gets it.
  const [insertAttestationGraphQL, graphqlState] = useMutation(
    InsertObligationChangeAttestationOneDocument,
    { update: (cache) => evictField(cache, 'obligation_change') }
  );

  // tRPC mutation
  const trpcMutation = useInsertObligationChangeAttestationTRPC();

  const insertObligationChangeAttestation = async (
    variables: InsertObligationChangeAttestationTRPCInput
  ): Promise<InsertObligationChangeAttestationCompatibilityResult> => {
    if (trpcEnabled) {
      return trpcMutation.insertObligationChangeAttestation(variables);
    }

    // The legacy Hasura action needs UserId, derived from the authenticated
    // user (the server derives it on the tRPC path).
    const result = await insertAttestationGraphQL({
      variables: {
        object: {
          ObligationChangeId: variables.ObligationChangeId,
          UserId: user!.userId,
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to insert obligation change attestation');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertObligationChangeAttestation,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertObligationChangeAttestation,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
